import { useState, useRef, useCallback, useEffect } from "react";
import {
  getSession,
  upSertSession,
  postGetPaymentForm,
} from "../../../../../../core/api/Api";
import { showToast } from "../../../../../../core/common-service/CommonService";
import { CATEGORY_ID_TO_LABEL } from "../../../../../../core/constants/constants";

export const useQuestionsData = (service_session_id, isLoggedIn) => {
  const [checkpoint_data, setCheckpointData] = useState({});
  const [data, setData] = useState([]);
  const [synopsis, setSynopsis] = useState([]);
  const [introduction, setIntroduction] = useState([]);
  const [summary, setSummary] = useState([]);
  const [bookTitle, setBookTitle] = useState([]);
  const [bookType, setBookType] = useState(""); // Added bookType state
  const [survey_questions, setSurveyQuestions] = useState([]);
  const [services_with_plans, setServicesWithPlans] = useState([]);
  const [general_category_id, setGeneralCategoryId] = useState(null);
  const [category_based_type_id, setCategoryBasedTypeId] = useState(null);
  const [category_length_type_id, setCategoryLengthTypeId] = useState(null);
  const [keyCharacters, setKeyCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [progressBarPercentage, setProgressBarPercentage] = useState(0);

  const mounted = useRef(true);
  const updateSessionTimeoutRef = useRef(null);
  const isLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const updateCircularProgressBar = useCallback((chapters) => {
    const total = chapters.length;
    const completed = chapters.filter(
      (c) => c.generated_content && c.generated_content.trim() !== ""
    ).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    setProgressBarPercentage(percentage);
  }, []);

  const updateSessionData = useCallback(() => {
    var tmp_checkpoint_data = { ...checkpoint_data };
    var tmp_data = [...data];
    if (tmp_data.length > 0) {
      tmp_checkpoint_data.data = tmp_data;
      clearTimeout(updateSessionTimeoutRef.current);
      updateSessionTimeoutRef.current = setTimeout(() => {
        if (isLoggedInRef.current) {
          upSertSession({
            service_session_id,
            checkpoint_data: tmp_checkpoint_data,
          })
            .then((res) => {})
            .catch((e) => {});
        } else {
          let trial_session = JSON.parse(localStorage.getItem("trial_session"));
          trial_session.checkpoint_data = tmp_checkpoint_data;
          localStorage.setItem("trial_session", JSON.stringify(trial_session));
        }
      }, 2000);
    }
  }, [checkpoint_data, data, service_session_id]);

  const onGetDataSuccess = useCallback(
    (response, chapter_to_add, index_chapter_to_add) => {
      let intro = response.introduction ?? " ";
      setIntroduction(intro);
      let summary = response.book_summary ?? " ";
      setSummary(summary);
      setBookTitle(response?.book_name ?? "");
      if (mounted.current && response) {
        var data = { ...response?.checkpoint_data };
        // check if there is id or not
        let nextId = Math.max(
          ...data.data.map((item) =>
            item.id !== undefined ? item.id : -Infinity
          )
        );
        data.data.forEach((item) => {
          if (!item?.id) {
            item.id = ++nextId;
          }
        });

        setCheckpointData(data ?? {});
        if (chapter_to_add) {
          data.data.splice(index_chapter_to_add + 1, 0, chapter_to_add);
          data.data.push(chapter_to_add);
        }
        setData(data?.data);
        setSynopsis(data?.another_story_subject);
        if (data?.data?.length > 0) {
          // setActiveCollapse callback would be called from main component
        }
        setSurveyQuestions(data?.lookups?.survey_questions);

        const generalCategoryId = data?.general_category_id;
        setGeneralCategoryId(generalCategoryId);

        // Extract and set book type
        const categoryLabel = CATEGORY_ID_TO_LABEL[generalCategoryId];
        setBookType(categoryLabel || "");

        setCategoryBasedTypeId(
          Array.isArray(data?.category_based_type_ids) &&
            data.category_based_type_ids.length > 0
            ? data.category_based_type_ids[0]
            : data?.category_based_type_id ?? null
        );
        setCategoryLengthTypeId(data?.category_length_type_id);
        setKeyCharacters(Array.isArray(response?.key_characters) ? response.key_characters : []);
        setIsPaid(response?.is_paid);
        setServicesWithPlans(data?.services_with_plans);
        updateCircularProgressBar(data?.data);
      }
    },
    [updateCircularProgressBar]
  );

  const getData = useCallback(
    (chapter_to_add, index_chapter_to_add) => {
      setLoading(true);
      if (isLoggedIn) {
        getSession(service_session_id)
          .then((res) => {
            setLoading(false);
            const response = res?.data?.success;
            onGetDataSuccess(response, chapter_to_add, index_chapter_to_add);
          })
          .catch((e) => {
            if (mounted.current) setLoading(false);
          });
      } else {
        setLoading(false);
        try {
          if (localStorage.getItem("trial_session")) {
            let response = JSON.parse(localStorage.getItem("trial_session"));
            onGetDataSuccess(response, chapter_to_add, index_chapter_to_add);
          }
        } catch (error) {
          showToast({ type: "error", message: "Error in trial session" });
        }
      }
      window.scrollTo(0, 0);
    },
    [isLoggedIn, onGetDataSuccess, service_session_id]
  );

  const getPaymentForm = useCallback(() => {
    if (service_session_id === 0 || service_session_id === "0") return;
    const obj = { service_session_id };
    postGetPaymentForm(obj)
      .then((res) => {
        const response = res?.data?.success;
        if (mounted.current && response) {
        } else {
        }
      })
      .catch((e) => {});
  }, [service_session_id]);

  return {
    // State
    checkpoint_data,
    setCheckpointData,
    data,
    setData,
    synopsis,
    setSynopsis,
    introduction,
    setIntroduction,
    summary,
    setSummary,
    bookTitle,
    setBookTitle,
    bookType, // Added bookType to return
    setBookType, // Added setBookType to return
    survey_questions,
    setSurveyQuestions,
    services_with_plans,
    setServicesWithPlans,
    general_category_id,
    setGeneralCategoryId,
    category_based_type_id,
    setCategoryBasedTypeId,
    category_length_type_id,
    setCategoryLengthTypeId,
    keyCharacters,
    setKeyCharacters,
    loading,
    setLoading,
    isPaid,
    setIsPaid,
    progressBarPercentage,
    setProgressBarPercentage,
    // Refs
    mounted,
    updateSessionTimeoutRef,
    isLoggedInRef,
    // Functions
    updateSessionData,
    getData,
    getPaymentForm,
    updateCircularProgressBar,
  };
};
